// Pure state of the questions step (CONTRACTS §7.4).
import {
  DocumentCraftingAnswer,
  DocumentCraftingAnswersPayload,
  DocumentCraftingQuestion,
} from "../../../Models/documentCrafting";
import { isValidUrl } from "../Create/documentFormModel";

export interface AnswerState {
  /** text, date, url: string; number: number | null; single_choice: the
   * picked option ("" = none); multi_choice: the picked options. */
  value: unknown;
  /** Free text next to the suggested options of a choice ("other"); for
   * multi_choice comma separated. */
  other: string;
  aiDecides: boolean;
}
export type AnswersState = Record<string, AnswerState>;

const isChoice = (question: DocumentCraftingQuestion) =>
  (question.answer_type === "single_choice" || question.answer_type === "multi_choice") &&
  (question.options ?? []).length > 0;

const splitOther = (other: string) =>
  other
    .split(",")
    .map((item) => item.trim())
    .filter(Boolean);

const emptyAnswer = (question: DocumentCraftingQuestion): AnswerState => ({
  value: question.answer_type === "multi_choice" ? [] : question.answer_type === "number" ? null : "",
  other: "",
  aiDecides: false,
});

/** Starts from the stored answers (e.g. after a retry). */
export const initialAnswers = (questions: DocumentCraftingQuestion[]): AnswersState => {
  const state: AnswersState = {};
  questions.forEach((question) => {
    const answer = question.answer;
    const empty = emptyAnswer(question);
    if (answer && "ai_decides" in answer && answer.ai_decides) {
      state[question.id] = { ...empty, aiDecides: true };
      return;
    }
    if (!answer || !("value" in answer) || answer.value === null || answer.value === undefined) {
      state[question.id] = empty;
      return;
    }
    const options = question.options ?? [];
    if (isChoice(question) && question.answer_type === "multi_choice") {
      const values = (Array.isArray(answer.value) ? answer.value : [answer.value]).map(String);
      state[question.id] = {
        ...empty,
        value: values.filter((item) => options.includes(item)),
        other: values.filter((item) => !options.includes(item)).join(", "),
      };
    } else if (isChoice(question)) {
      const value = String(answer.value);
      state[question.id] = options.includes(value)
        ? { ...empty, value }
        : { ...empty, other: value };
    } else {
      state[question.id] = { ...empty, value: answer.value };
    }
  });
  return state;
};

/** What the answer amounts to (picked options plus the free text). */
export const effectiveValue = (question: DocumentCraftingQuestion, answer: AnswerState): unknown => {
  if (isChoice(question) && question.answer_type === "multi_choice") {
    const picked = Array.isArray(answer.value) ? answer.value.map(String) : [];
    return [...picked, ...splitOther(answer.other)];
  }
  if (isChoice(question)) {
    return answer.other.trim() || (typeof answer.value === "string" ? answer.value : "");
  }
  if (question.answer_type === "multi_choice") {
    // No suggested options: a comma separated free text.
    return typeof answer.value === "string" ? splitOther(answer.value) : answer.value;
  }
  return answer.value;
};

/** Whether a value is usable for the question (filled in and well-formed). */
export const hasValidValue = (question: DocumentCraftingQuestion, value: unknown) => {
  if (Array.isArray(value)) return value.some((item) => String(item).trim());
  if (question.answer_type === "number") {
    return typeof value === "number" && Number.isFinite(value);
  }
  const text = typeof value === "string" ? value.trim() : "";
  if (!text) return false;
  if (question.answer_type === "url") return isValidUrl(text);
  if (question.answer_type === "date") return /^\d{4}-\d{2}-\d{2}$/.test(text);
  return true;
};

export const isAnswered = (question: DocumentCraftingQuestion, state: AnswersState) => {
  const answer = state[question.id];
  return !!answer && (answer.aiDecides || hasValidValue(question, effectiveValue(question, answer)));
};

export const answeredCount = (questions: DocumentCraftingQuestion[], state: AnswersState) =>
  questions.filter((question) => isAnswered(question, state)).length;

export const buildAnswersPayload = (
  questions: DocumentCraftingQuestion[],
  state: AnswersState,
  outlineRemarks: string,
): DocumentCraftingAnswersPayload => {
  const answers: Record<string, DocumentCraftingAnswer> = {};
  questions.forEach((question) => {
    const answer = state[question.id];
    if (!answer || answer.aiDecides) {
      answers[question.id] = { ai_decides: true };
      return;
    }
    const value = effectiveValue(question, answer);
    answers[question.id] = {
      value: typeof value === "string" ? value.trim() : value,
    };
  });
  const remarks = outlineRemarks.trim();
  return remarks ? { answers, outline_remarks: remarks } : { answers };
};
